import { useCallback, useMemo } from 'react'
import { buildErrorMessage } from '@constants'
import { AxiosError } from 'axios'
import useIsPlanView from '@editor/hooks/useIsPlanView'
import usePlanTablesQueryKey from '@editor/hooks/usePlanTablesQueryKey'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useSnackbar } from '@ui/snackbar'
import { useCommentFilterStore, useControlStore, useModelStore } from '@editorStores'
import { getPlanTables } from '@queries'
import type { PlanTableDTO } from '@mutations'
import type { PlanTable, TableData } from 'src/domain/planTables/types'
import { isTemplatePlanTable } from 'src/domain/planTables/types'
import { usePersistentParams } from 'src/hooks'
import { getPlanViewExportStoreys, getPlanViewStoreys, toPlanViewPlanType } from '../utils'

/** Marks the plan table queries resolved for the Planansicht's storeys (see `usePlanTableMutations`). */
export const PLAN_VIEW_PLAN_TABLES_QUERY_KEY = 'planViewStoreys'

interface Options {
  /**
   * Fetch the storey-resolved tables (and report failures). Exactly one mounted consumer — the
   * scene's `PlanTableController` — fetches; every other one reads the same cached query.
   */
  fetch?: boolean
}

export interface PlanViewPlanTables {
  /** Whether the scene previews a plan ("Planansicht"), so tables are drawn as in the PDF. */
  isPlanView: boolean
  /** The table content the plan export draws for `planTable`. */
  getTableData: (planTable: PlanTable) => TableData
  /** Whether the export of the previewed plan includes `planTable` (always true outside it). */
  isExportedInPreview: (planTable: PlanTable) => boolean
  /**
   * Reload the tables the scene shows (the storey-resolved ones in the Planansicht, the stored
   * ones elsewhere), re-resolving the template tables from the current project data.
   */
  refresh: () => Promise<void>
}

/**
 * Plan tables as the export of the previewed plan has them. Template tables are resolved on the
 * server for exactly the storeys the export covers — the default plan table read resolves them
 * for fallback storeys, so their rows could differ from the PDF — and tables not exported to
 * those storeys are left out like `filter_plan_tables_for_plan_export` does. Outside the
 * Planansicht nothing is fetched and the stored tables are used as they are.
 */
const usePlanViewPlanTables = ({ fetch = false }: Options = {}): PlanViewPlanTables => {
  const isPlanView = useIsPlanView()
  const floorplanStorey = useControlStore(state => state.floorplanState.storey)
  const visibleStoreys = useModelStore(state => state.visibleStoreys)
  const selectedPlan = useCommentFilterStore(state => state.selectedPlan)
  const { projectId }: { projectId?: string } = usePersistentParams()
  const planTablesQueryKey = usePlanTablesQueryKey(projectId)
  const { enqueueSnackbar } = useSnackbar()
  const queryClient = useQueryClient()

  const exportStoreys = useMemo(
    () =>
      isPlanView
        ? getPlanViewExportStoreys(
            toPlanViewPlanType(selectedPlan),
            getPlanViewStoreys(floorplanStorey, visibleStoreys),
          )
        : null,
    [isPlanView, selectedPlan, floorplanStorey, visibleStoreys],
  )

  const { data, refetch } = useQuery<PlanTableDTO[], AxiosError>({
    queryKey: [...planTablesQueryKey, PLAN_VIEW_PLAN_TABLES_QUERY_KEY, exportStoreys],
    queryFn: () => getPlanTables.request(projectId, exportStoreys ?? undefined),
    enabled: fetch && !!projectId && !!exportStoreys?.length,
    // Keep the previous storey's rows while the next storey resolves instead of jumping back to
    // the fallback content.
    keepPreviousData: true,
    onError: (error: AxiosError) => {
      enqueueSnackbar(buildErrorMessage(error, 'Fehler beim Laden der Plan-Tabellen'), {
        variant: 'error',
      })
    },
  })

  const resolvedTables = useMemo(() => {
    const lookup = new Map<string, TableData>()
    if (!exportStoreys) return lookup
    data?.forEach(dto => lookup.set(dto.id, dto.table))
    return lookup
  }, [data, exportStoreys])

  // Custom tables keep the stored content: that is what the user edits, and it does not depend
  // on the storey.
  const getTableData = useCallback(
    (planTable: PlanTable): TableData =>
      isTemplatePlanTable(planTable)
        ? resolvedTables.get(planTable.id) ?? planTable.table
        : planTable.table,
    [resolvedTables],
  )

  const isExportedInPreview = useCallback(
    (planTable: PlanTable): boolean =>
      // Without a previewed storey yet (e.g. while the Planansicht initialises) nothing is filtered.
      !exportStoreys?.length ||
      exportStoreys.some(storey => planTable.planConfig.storeysForPlans.includes(storey)),
    [exportStoreys],
  )

  // Failures surface through the queries' own `onError` snackbars.
  const refresh = useCallback(async (): Promise<void> => {
    if (exportStoreys?.length) {
      await refetch()
      return
    }
    await queryClient.refetchQueries({
      queryKey: getPlanTables.getKey(projectId),
      type: 'active',
      predicate: query => !query.queryKey.includes(PLAN_VIEW_PLAN_TABLES_QUERY_KEY),
    })
  }, [exportStoreys, refetch, queryClient, projectId])

  return { isPlanView, getTableData, isExportedInPreview, refresh }
}

export default usePlanViewPlanTables
